import * as Headless from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import { Fragment } from "react";

type SearchOptionDropdownProps<T> = {
    className?: string;
    placeholder?: React.ReactNode;
    autoFocus?: boolean;
    "aria-label"?: string;
    children?: React.ReactNode;
} & Omit<Headless.ListboxProps<typeof Fragment, T>, "as" | "multiple">;

const SearchOptionDropdown = <T,>({
    className,
    placeholder,
    autoFocus,
    "aria-label": ariaLabel,
    children: options,
    ...props
}: SearchOptionDropdownProps<T>) => {
    return (
        <Headless.Listbox {...props} multiple={false}>
            <Headless.ListboxButton
                autoFocus={autoFocus}
                data-slot="control"
                aria-label={ariaLabel}
                className={clsx([
                    className,
                    "group relative flex w-fit pl-5 pr-1 cursor-pointer",
                    "before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-white before:shadow-sm",
                    "before:hidden",
                    "focus:outline-hidden",
                    "after:pointer-events-none after:absolute after:inset-0 after:rounded-l-full after:ring-transparent after:ring-inset data-focus:after:ring-2 data-focus:after:ring-primary-500",
                    "data-disabled:opacity-50 data-disabled:before:bg-zinc-950/5 data-disabled:before:shadow-none",
                ])}
            >
                <Headless.ListboxSelectedOption
                    as="span"
                    options={options}
                    placeholder={placeholder && <span className="flex truncate text-zinc-500">{placeholder}</span>}
                    className={clsx([
                        "relative flex w-full appearance-none rounded-l-full py-[calc(--spacing(2.5)-1px)] sm:py-[calc(--spacing(1.5)-1px)]",
                        "min-h-11 sm:min-h-9",
                        "pr-[calc(--spacing(6.5)-1px)] pl-[calc(--spacing(1)-1px)]",
                        "text-left text-base/6 text-zinc-950 placeholder:text-zinc-500 forced-colors:text-[CanvasText] font-medium",
                        "border-0",
                        "bg-transparent",
                        "group-data-invalid:border-red-500 group-data-hover:group-data-invalid:border-red-500",
                        "group-data-disabled:border-zinc-950/20 group-data-disabled:opacity-100",
                    ])}
                />
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDownIcon className="size-5 stroke-black group-data-disabled:stroke-zinc-600 sm:size-4  forced-colors:stroke-[CanvasText]" />
                </span>
            </Headless.ListboxButton>
            <Headless.ListboxOptions
                transition
                anchor="top end"
                modal={false}
                className={clsx(
                    "-mt-1 z-50 p-4",
                    "isolate w-max scroll-py-1 rounded-xl p-1 select-none",
                    "outline outline-transparent focus:outline-hidden",
                    "overflow-y-scroll overscroll-contain",
                    "bg-white backdrop-blur-xl",
                    "ring-1 shadow-lg ring-[#E6E8EC]",
                    "transition-opacity duration-100 ease-in data-closed:data-leave:opacity-0 data-transition:pointer-events-none",
                    "grid grid-cols-1"
                )}
            >
                {options}
            </Headless.ListboxOptions>
        </Headless.Listbox>
    );
};

export default SearchOptionDropdown;
